import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import api from '../../services/api';
import { useOrg } from '../../contexts/OrgContext';

const TYPE_COLORS = {
  info: { bg: 'bg-blue-600', text: 'text-blue-50' },
  warning: { bg: 'bg-amber-500', text: 'text-amber-50' },
  success: { bg: 'bg-green-600', text: 'text-green-50' },
  urgent: { bg: 'bg-red-600', text: 'text-red-50' },
};

// Scroll SPEED (px/second) — every notice moves across the screen at this
// pace. The loop duration is computed from the measured text width:
// duration = width / SPEED, so short and long notices scroll evenly.
// (News-ticker pace — slow enough to read comfortably.)
const MARQUEE_SPEED_PX_PER_SEC = 80;
// One full loop of the notice line never runs quicker than this.
const MARQUEE_MIN_DURATION_SEC = 3;

// HOW THE CONTINUOUS TICKER WORKS (two phases, zero gaps):
//
// PHASE 1 — ENTRY (runs once, on page load):
//   The whole notice line starts just past the RIGHT edge of the screen and
//   slides left until its head reaches the left edge — so the first notice
//   ENTERS FROM THE RIGHT like a news ticker.
//
// PHASE 2 — ENDLESS LOOP (runs forever):
//   The line is built from identical copies of ALL notices (LAST notice of
//   the list → FIRST notice, one after one). Each cycle the line slides left
//   by exactly ONE copy-width. Because the content repeats every copy-width,
//   the jump back to the start is pixel-for-pixel identical — so notices run
//   last → first, wrap back to last, and repeat endlessly with NO GAP ever.
const marqueeStyles = `
@keyframes marqueeEnter {
  0% { transform: translateX(var(--marquee-container-w, 100vw)); }
  100% { transform: translateX(0px); }
}
@keyframes marqueeLoop {
  0% { transform: translateX(0px); }
  100% { transform: translateX(calc(-1 * var(--marquee-copy-w, 0px))); }
}
.marquee-scroll {
  display: inline-flex;
  white-space: nowrap;
  will-change: transform;
}
.marquee-enter {
  animation: marqueeEnter linear 1 both;
}
.marquee-loop {
  animation: marqueeLoop linear infinite;
}
.marquee-scroll:hover {
  animation-play-state: paused;
}
`;

const MarqueeBar = () => {
  const [notices, setNotices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Measured sizes: width of ONE copy of the line, width of the visible bar,
  // and how many copies are needed to keep the bar always full.
  const [metrics, setMetrics] = useState(null);
  // 'measure' → 'enter' (slide in from right, once) → 'loop' (endless).
  const [phase, setPhase] = useState('measure');
  const wrapperRef = useRef(null);
  const lineRef = useRef(null);

  const {
    daysRemaining,
    isExpired,
    isTrial,
    plan,
    subscriptionEndsAt,
    trialEndsAt,
  } = useOrg();

  // Build subscription warning notices (system messages, always shown first)
  const subscriptionNotices = useMemo(() => {
    const list = [];

    if (isExpired) {
      // Subscription has expired — show urgent notice
      const planName = plan?.name || 'your plan';
      list.push({
        _id: 'sub-expired',
        title: '⚠️ Subscription Expired',
        message: `Your ${planName} subscription has expired. Access has been suspended. Renew now to restore service.`,
        type: 'urgent',
        priority: 999,
      });
    } else if (daysRemaining != null && daysRemaining <= 5 && daysRemaining >= 0) {
      // Subscription expiring within 5 days — only when an actual end date
      // exists (daysRemaining is null when there is no end date)
      const planName = plan?.name || 'your plan';
      const endDate = subscriptionEndsAt || trialEndsAt;
      const formattedDate = endDate
        ? new Date(endDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
        : 'soon';

      const isTrialPlan = isTrial;
      const subType = isTrialPlan ? 'free trial' : planName;

      if (daysRemaining === 0) {
        list.push({
          _id: 'sub-expiring-today',
          title: '🔴 Subscription Expires TODAY',
          message: `Your ${subType} expires today (${formattedDate}). Renew now to avoid service interruption!`,
          type: 'urgent',
          priority: 998,
        });
      } else if (daysRemaining === 1) {
        list.push({
          _id: 'sub-expiring-1d',
          title: '⚠️ Subscription Expires Tomorrow',
          message: `Your ${subType} expires tomorrow (${formattedDate}). Renew now to avoid any interruption.`,
          type: 'urgent',
          priority: 995 + daysRemaining,
        });
      } else if (daysRemaining === 2) {
        list.push({
          _id: 'sub-expiring-2d',
          title: '⏰ Subscription Expiring Soon',
          message: `Your ${subType} expires in ${daysRemaining} days (${formattedDate}). Renew now to avoid service interruption.`,
          type: 'warning',
          priority: 995 + daysRemaining,
        });
      } else {
        // 3, 4, 5 days
        list.push({
          _id: `sub-expiring-${daysRemaining}d`,
          title: '⏰ Subscription Expiring Soon',
          message: `Your ${subType} expires in ${daysRemaining} days (${formattedDate}). Renew now to avoid service interruption.`,
          type: daysRemaining <= 2 ? 'urgent' : 'warning',
          priority: 990 + daysRemaining,
        });
      }
    }

    return list;
  }, [isExpired, daysRemaining, isTrial, plan, subscriptionEndsAt, trialEndsAt]);

  // Fetch backend notices (superadmin announcements)
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const response = await api.get('/notices/active');
        const data = response.data?.data || response.data || [];
        const apiList = Array.isArray(data) ? data : [];
        // REVERSED display order — the stream starts from the LAST notice of
        // the list (last point of the notices section), goes one after one up
        // to the FIRST notice, then wraps back to the last again, forever.
        const noticeList = apiList.slice().reverse();
        setNotices(noticeList);
      } catch (error) {
        console.warn('Failed to fetch notices:', error?.response?.status || error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotices();
    const interval = setInterval(fetchNotices, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // All notices in display order: subscription warnings first, then the
  // superadmin notices in LAST → FIRST order.
  const allNotices = useMemo(
    () => [...subscriptionNotices, ...notices],
    [subscriptionNotices, notices],
  );

  // Measure ONE copy of the line and the visible bar width. Render just enough
  // copies that the bar is ALWAYS completely covered, in every phase.
  useLayoutEffect(() => {
    const el = lineRef.current;
    const wrap = wrapperRef.current;
    if (!el || !wrap) return;
    const copiesNow = metrics?.copies || 1;
    const copyWidth = el.scrollWidth / copiesNow;
    const containerWidth = wrap.offsetWidth;
    if (!copyWidth || !containerWidth) return;
    const copies = Math.max(2, Math.ceil(containerWidth / copyWidth) + 1);
    if (
      !metrics ||
      metrics.copyWidth !== copyWidth ||
      metrics.containerWidth !== containerWidth ||
      metrics.copies !== copies
    ) {
      setMetrics({ copyWidth, containerWidth, copies });
      setPhase('enter');
    }
  }, [allNotices, metrics]);

  // PHASE 1 → PHASE 2: when the slide-in finishes, start the endless loop
  // from the exact same position (translateX(0)) — no jump, no gap.
  const handleEnterEnd = () => setPhase('loop');

  if (isLoading || allNotices.length === 0) return null;

  // Bar colour comes from the first notice that enters the line.
  const colors = TYPE_COLORS[allNotices[0].type] || TYPE_COLORS.info;
  // Changing this key remounts the line when the notice list changes.
  const lineKey = allNotices.map((n) => n._id).join('|');

  const copies = metrics?.copies || 1;
  const enterDuration = metrics
    ? metrics.containerWidth / MARQUEE_SPEED_PX_PER_SEC
    : 0;
  const loopDuration = metrics
    ? Math.max(MARQUEE_MIN_DURATION_SEC, metrics.copyWidth / MARQUEE_SPEED_PX_PER_SEC)
    : 0;

  const phaseClass =
    phase === 'enter' ? 'marquee-enter' : phase === 'loop' ? 'marquee-loop' : '';

  return (
    <>
      <style>{marqueeStyles}</style>
      <div className={`${colors.bg} ${colors.text}`}>
        <div className="w-full">
          <div ref={wrapperRef} className="flex items-center h-9 overflow-hidden">
            <div
              key={lineKey}
              ref={lineRef}
              className={`marquee-scroll ${phaseClass}`}
              style={{
                '--marquee-container-w': metrics
                  ? `${metrics.containerWidth}px`
                  : '100vw',
                '--marquee-copy-w': metrics ? `${metrics.copyWidth}px` : '0px',
                animationDuration:
                  phase === 'enter'
                    ? `${enterDuration}s`
                    : phase === 'loop'
                      ? `${loopDuration}s`
                      : undefined,
                // Hidden until measured, so the first frame never jumps.
                visibility: metrics ? 'visible' : 'hidden',
              }}
              onAnimationEnd={phase === 'enter' ? handleEnterEnd : undefined}
            >
              {Array.from({ length: copies }).map((_, copy) => (
                <div key={copy} className="flex items-center" aria-hidden={copy > 0}>
                  {allNotices.map((n, i) => (
                    <span
                      key={`${copy}-${n._id || i}-${i}`}
                      className="mx-8 shrink-0"
                    >
                      {`${n.title} — ${n.message}`}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarqueeBar;